// Ancestry is metadata, never inferred from proximity or a layer name.
export const derivedFrom = (operation, inputs) => ({ operation, inputs: [...new Set(inputs)] })

export function recordEditorLineage(previous, next) {
  const nodes = { ...(previous.lineage ?? {}), ...(next.lineage ?? {}) }
  const before = new Set([...(previous.userPolylines ?? []), ...(previous.preprocessedMods ?? []), ...(previous.preprocessedEdits ?? [])].map(e => e.handle))
  for (const e of [...(next.userPolylines ?? []), ...(next.preprocessedMods ?? []), ...(next.preprocessedEdits ?? [])]) {
    if (e.handle && !before.has(e.handle) && !nodes[e.handle]) {
      nodes[e.handle] = e.lineage ?? derivedFrom('generate', [])
    }
  }
  return { ...next, lineage: nodes }
}

export function buildLineageReport(documentId, lineage, edit, entities) {
  const raw = lineage?.raw ?? {}
  const nodes = { ...(lineage?.nodes ?? {}), ...(edit?.lineage ?? {}) }
  const memo = new Map()
  const resolve = (id, visiting = new Set()) => {
    if (memo.has(id)) return memo.get(id)
    if (visiting.has(id)) return { sources: [], operations: [], unknown: true }
    const n = nodes[id]
    if (raw[id]) return { sources: [id], operations: ['raw'], unknown: false }
    if (!n) return { sources: [], operations: [], unknown: true }
    const path = new Set(visiting).add(id)
    const parents = (n.inputs ?? []).map(h => resolve(h, path))
    const result = {
      sources: [...new Set(parents.flatMap(p => p.sources))],
      operations: [...new Set([...parents.flatMap(p => p.operations), n.operation])],
      unknown: !!n.incomplete || n.operation === 'unknown' || parents.some(p => p.unknown) || (!(n.inputs?.length) && n.operation !== 'generate'),
    }
    memo.set(id, result)
    return result
  }
  const outputs = (entities ?? []).map(e => ({
    entity_id: e.handle, current_class: e.layer, ...resolve(e.handle),
  }))
  const descendants = new Map()
  for (const output of outputs) for (const source of output.sources) {
    if (!descendants.has(source)) descendants.set(source, [])
    descendants.get(source).push({ entity_id: output.entity_id, current_class: output.current_class })
  }
  const deleted = new Set(edit?.deletedHandles ?? [])
  const deletedSources = new Set([...deleted].flatMap(h => resolve(h).sources))
  const sources = Object.values(raw).map(source => ({ ...source,
    descendants: descendants.get(source.handle) ?? [],
    status: descendants.has(source.handle) ? 'mapped' : deletedSources.has(source.handle) ? 'deleted_in_editor' : nodes[source.handle]?.status ?? 'unknown',
    reason: nodes[source.handle]?.reason,
  }))
  return { version: 1, document_id: documentId ?? null, inventory_basis: lineage?.inventory_basis ?? 'unknown',
    recovery_errors: lineage?.recovery_errors ?? null, original_view_fallback: lineage?.original_view_fallback ?? null,
    sources, outputs, nodes, deleted_entity_ids: [...deleted] }
}

export function downloadLineage(report, exported, filename) {
  const blob = new Blob([JSON.stringify({ ...report, exported }, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = Object.assign(document.createElement('a'), { href: url, download: filename })
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
}
